// ============================================================================
// Sidebar (session history)
// ============================================================================
import { sbList, inner } from "./dom.js";
import { clearCachedSession } from "./cache.js";
import { wireFileLinks } from "./file-viewer.js";
import { deleteSessionPins } from "./pins.js";

// Deps populated by initSidebar()
let getSessions = () => [];
let getLiveSessionIds = () => new Set();
let getCurrentSessionId = () => null;
let getDeletedSessionIds = () => new Set();
let getAgentMsgs = () => new Map();
let getToolCards = () => new Map();
let getLoadingHistory = () => false;
let setLoadingHistory = null;
let getCacheRestoreToken = () => 0;
let incrementCacheRestoreToken = null;
let getResuming = () => false;
let setResuming = null;
let getExpectedSessionId = () => null;
let setExpectedSessionId = null;
let getSelectedDir = () => "";
let setSelectedDir = null;
let getUserPinnedAway = () => false;
let setUserPinnedAway = () => {};
let getSessionActive = () => false;
let setSessionActive = null;
let sendRef = null;
let setStatusRef = null;
let setActiveCwdRef = null;
let setSessionIdRef = null;
let showOnboardingRef = null;
let resetNodeCacheStateRef = null;
let restoreCachedNodesRef = null;
let scrollDownRef = null;
let sysMsgRef = null;

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = Date.now();
  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function renderSidebar() {
  const sessions = getSessions();
  const liveSessionIds = getLiveSessionIds();
  const currentSessionId = getCurrentSessionId();
  sbList.innerHTML = "";
  if (sessions.length === 0) {
    const empty = document.createElement("div");
    empty.className = "sb-empty";
    empty.textContent = "No conversations yet";
    sbList.appendChild(empty);
    return;
  }
  const section = document.createElement("div");
  section.className = "sb-section";
  section.textContent = "Recents";
  sbList.appendChild(section);
  for (const s of sessions) {
    const item = document.createElement("div");
    const isLive = liveSessionIds.has(s.sessionId);
    item.className = "sb-item" + (s.sessionId === currentSessionId ? " active" : "");
    const info = document.createElement("div");
    info.className = "si-info";
    const headerRow = document.createElement("div");
    headerRow.className = "si-header-row";
    const title = document.createElement("div");
    title.className = "si-title";
    title.textContent = s.displayName || s.title || s.sessionId.slice(0, 16);
    headerRow.appendChild(title);
    if (s.updatedAt) {
      const timeEl = document.createElement("span");
      timeEl.className = "si-time";
      timeEl.textContent = formatRelativeTime(s.updatedAt);
      headerRow.appendChild(timeEl);
    }
    info.appendChild(headerRow);
    // Topic or snippet preview subtitle
    const subtitle = s.topic || (s.cwd ? s.cwd.replace(/^\/home\/[^/]+/, "~") : "");
    if (subtitle) {
      const topicEl = document.createElement("div");
      topicEl.className = "si-topic";
      topicEl.textContent = subtitle;
      info.appendChild(topicEl);
    }
    info.onclick = () => { haptic("tap"); loadSessionFromHistory(s.sessionId); };
    item.appendChild(info);
    // Live indicator (green bubble) + stop button for sessions with a running process
    if (isLive) {
      const liveWrap = document.createElement("div");
      liveWrap.className = "si-live";
      const dot = document.createElement("span");
      dot.className = "si-live-dot";
      dot.title = "Session process is running";
      const stopBtn = document.createElement("button");
      stopBtn.className = "si-stop";
      stopBtn.title = "Stop session process";
      stopBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>';
      stopBtn.onclick = (e) => {
        e.stopPropagation();
        haptic("press");
        // Disable button immediately — the server waits for the process
        // to exit before sending session_stopped, so there's a brief delay.
        stopBtn.disabled = true;
        stopBtn.style.opacity = "0.5";
        if (s.sessionId === getCurrentSessionId()) {
          setStatusRef("err", "stopping…");
        }
        sendRef({ type: "stop_session", sessionId: s.sessionId });
      };
      liveWrap.appendChild(dot);
      liveWrap.appendChild(stopBtn);
      item.appendChild(liveWrap);
    }
    // Delete button
    const delBtn = document.createElement("button");
    delBtn.className = "si-delete";
    delBtn.title = "Delete chat";
    delBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    delBtn.onclick = (e) => { haptic("press");
      e.stopPropagation();
      deleteSession(s.sessionId, s.displayName || s.title || s.sessionId.slice(0, 16));
    };
    item.appendChild(delBtn);
    sbList.appendChild(item);
  }
}

export function deleteSession(sessionId, title) {
  // Use a simple confirm via a small inline modal
  const overlay = document.createElement("div");
  overlay.className = "overlay";
  overlay.innerHTML = `
    <div class="modal" style="width:min(400px,92vw)">
      <div class="modal-head">
        <h2>Delete chat?</h2>
      </div>
      <div style="padding:16px">
        <p style="font-size:14px;color:var(--text-2);margin-bottom:16px">
          Are you sure you want to delete "<strong></strong>"? This cannot be undone.
        </p>
        <div style="display:flex;gap:8px;justify-content:flex-end">
          <button class="ghost" id="del-cancel">Cancel</button>
          <button class="danger" id="del-confirm">Delete</button>
        </div>
      </div>
    </div>
  `;
  overlay.querySelector("strong").textContent = title;
  document.body.appendChild(overlay);
  overlay.querySelector("#del-cancel").onclick = () => { haptic("tap"); overlay.remove(); };
  overlay.querySelector("#del-confirm").onclick = () => { haptic("press");
    overlay.remove();
    // Track as deleted so stale sessions_list responses don't re-add it
    getDeletedSessionIds().add(sessionId);
    // Optimistically remove from the local list and re-render immediately
    setSessions(sessionsFilter(getSessions(), sessionId));
    renderSidebar();
    sendRef({ type: "delete_session", sessionId });
    clearCachedSession(sessionId); // clean up IndexedDB
    deleteSessionPins(sessionId); // drop the session's pinned docs
    // If deleting the current session, start fresh
    if (sessionId === getCurrentSessionId()) {
      inner.innerHTML = "";
      getAgentMsgs().clear();
      getToolCards().clear();
      setSessionIdRef(null);
      localStorage.removeItem("devin-session-id");
      setSessionActive(false);
      setActiveCwdRef("");
      showOnboardingRef();
    }
  };
}

// Helper to filter out a session by id (avoids mutating the array in place
// in a way that would break the reference held by main.js).
let setSessions = null;
function sessionsFilter(arr, id) {
  return arr.filter((s) => s.sessionId !== id);
}

export function loadSessionFromHistory(sessionId) {
  if (sessionId === getCurrentSessionId()) return;
  // Clear the chat view — the server will replay ordered history.
  inner.innerHTML = "";
  getAgentMsgs().clear();
  getToolCards().clear();
  setUserPinnedAway(false);
  setLoadingHistory(true);
  // Invalidate any in-flight cache restore from a prior session switch.
  incrementCacheRestoreToken();
  const myToken = getCacheRestoreToken();
  // Set resuming + expectedSessionId so that:
  // 1. The "error" handler doesn't show the onboarding screen if the
  //    session load fails (e.g. stale lock from a stopped process).
  // 2. The "ready" handler can detect if a fallback new session was
  //    created instead of the requested session being resumed.
  setResuming(true);
  setExpectedSessionId(sessionId);
  // Look up the session's cwd from the sessions list so the server
  // can spawn the new process in the correct folder (cross-folder "Go to").
  const target = getSessions().find((s) => s.sessionId === sessionId);
  const targetCwd = target?.cwd || null;
  // Optimistically update the active folder so the UI feels responsive.
  if (targetCwd) {
    setSelectedDir(targetCwd);
    setActiveCwdRef(targetCwd);
  }
  // Reset per-node cache state for the new session and show cached nodes
  // instantly (partial restoration) while the server loads & replays.
  resetNodeCacheStateRef();
  restoreCachedNodesRef(sessionId).then((nodes) => {
    // Skip if a newer session switch or a replay start invalidated us.
    if (myToken !== getCacheRestoreToken()) return;
    if (!getLoadingHistory()) return;
    if (nodes.length > 0) {
      for (const n of nodes) {
        if (!n.html) continue;
        const wrap = document.createElement("div");
        wrap.innerHTML = n.html;
        // outerHTML was cached; the first child is the node itself.
        const el = wrap.firstElementChild;
        if (el) {
          // Cached HTML contains wired file-link anchors (href="#" +
          // data-wired="1") whose click listeners don't survive
          // serialization. Strip the stale wiring markers and re-wire so
          // restored citations are clickable, matching live rendering.
          el.querySelectorAll("a.file-link[data-wired]").forEach((a) => {
            a.removeAttribute("data-wired");
            a.removeAttribute("href");
          });
          wireFileLinks(el);
          inner.appendChild(el);
          if (el.classList.contains("tool-card") && el.dataset.id) {
            getToolCards().set(el.dataset.id, el);
          }
        }
      }
      scrollDownRef(true);
    }
  });
  sysMsgRef("Loading session…");
  sendRef({ type: "load_session", sessionId, cwd: targetCwd || undefined });
}

/**
 * Initialize the sidebar module.
 * @param {object} deps
 */
export function initSidebar(deps) {
  getSessions = deps.getSessions;
  setSessions = deps.setSessions;
  getLiveSessionIds = deps.getLiveSessionIds;
  getCurrentSessionId = deps.getCurrentSessionId;
  getDeletedSessionIds = deps.getDeletedSessionIds;
  getAgentMsgs = deps.getAgentMsgs;
  getToolCards = deps.getToolCards;
  getLoadingHistory = deps.getLoadingHistory;
  setLoadingHistory = deps.setLoadingHistory;
  getCacheRestoreToken = deps.getCacheRestoreToken;
  incrementCacheRestoreToken = deps.incrementCacheRestoreToken;
  getResuming = deps.getResuming;
  setResuming = deps.setResuming;
  getExpectedSessionId = deps.getExpectedSessionId;
  setExpectedSessionId = deps.setExpectedSessionId;
  getSelectedDir = deps.getSelectedDir;
  setSelectedDir = deps.setSelectedDir;
  getUserPinnedAway = deps.getUserPinnedAway;
  setUserPinnedAway = deps.setUserPinnedAway;
  getSessionActive = deps.getSessionActive;
  setSessionActive = deps.setSessionActive;
  sendRef = deps.send;
  setStatusRef = deps.setStatus;
  setActiveCwdRef = deps.setActiveCwd;
  setSessionIdRef = deps.setSessionId;
  showOnboardingRef = deps.showOnboarding;
  resetNodeCacheStateRef = deps.resetNodeCacheState;
  restoreCachedNodesRef = deps.restoreCachedNodes;
  scrollDownRef = deps.scrollDown;
  sysMsgRef = deps.sysMsg;
}
